import { assertNever } from '@kofno/piper';
import { just, Maybe, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Task } from 'taskarian';
import { callApi } from '../../../../../../Appy';
import { currentUserStore } from '../../../../../../CurrentUserStore';
import { EmailError, validateEmail } from '../../../../../../EmailAddress';
import { findLinkT } from '../../../../../../LinkyLinky';
import { errorAlert, FlashAlert } from '../../../../../../Notifications/Types';
import { Link } from '../../../../../../Resource/Types';
import { fromResult } from '../../../../../../TaskExt';
import { TPlainTextKey } from '../../../../../../Translations';
import { SelectableUserGroup } from '../../../../../Form/GroupInputWithSuggestions/Types';
import { SelectableLicense } from '../Types';
import { directoryParticipantResourceDecoder, participantResourceDecoder } from './Decoders';
import {
  creating,
  DirectoryParticipantResource,
  LookingForParticipant,
  lookingForParticipant,
  participantDirectoryLink,
  ParticipantError,
  ParticipantResource,
  ParticipantState,
} from './Types';

export class ParticipantStore {
  @observable state: ParticipantState = {
    kind: 'ready',
    participant: {
      email: '',
      firstName: '',
      lastName: '',
      userGroups: [],
      licenses: [],
    },
    areLicensesAuditor: false,
    formErrors: {},
    isExistingParticipant: nothing(),
    initialGroupCount: nothing(),
    initialLicenseCount: nothing(),
  };

  @observable lastLookedUpEmail: string = '';

  constructor() {
    makeObservable(this);
  }

  @action
  lookUpParticipant = () => {
    const state = this.state;
    const currentEmail = this.state.participant.email;

    // Skip lookup if we've already looked up this email
    if (currentEmail === this.lastLookedUpEmail) {
      return;
    }

    switch (state.kind) {
      case 'ready':
      case 'directory-lookup-error':
      case 'error':
        this.state = lookingForParticipant(state);
        this.fetchExistingParticipant(this.state, currentEmail);
        break;
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
        break;
      default:
        assertNever(state);
    }
  };

  private handleLookupSuccess =
    (state: LookingForParticipant) =>
    (directoryParticipantResource: DirectoryParticipantResource): void => {
      runInAction(() => {
        this.lastLookedUpEmail = directoryParticipantResource.payload.email;

        // Count existing groups and licenses from the response
        const existingGroups = directoryParticipantResource.payload.userGroups;
        const existingLicenses = directoryParticipantResource.payload.licenses;

        this.state = {
          kind: 'ready',
          participant: {
            email: directoryParticipantResource.payload.email,
            firstName: directoryParticipantResource.payload.firstName,
            lastName: directoryParticipantResource.payload.lastName,
            userGroups: existingGroups,
            licenses: existingLicenses,
          },
          areLicensesAuditor: state.areLicensesAuditor,
          formErrors: {},
          isExistingParticipant: just(true),
          initialGroupCount: just(existingGroups.length),
          initialLicenseCount: just(existingLicenses.length),
        };
      });
    };

  private fetchExistingParticipant = (state: LookingForParticipant, email: string) => {
    fromResult(validateEmail(email))
      .mapError((emailError: EmailError): ParticipantError => emailError)
      .andThen(() => Task.succeed(currentUserStore.links))
      .andThen(participantDirectoryLink(email))
      .andThen(callApi(directoryParticipantResourceDecoder, {}))
      .fork(this.handleLookupError(state), this.handleLookupSuccess(state));
  };

  private handleLookupError =
    (state: LookingForParticipant) =>
    (participantError: ParticipantError): void => {
      runInAction(() => {
        this.lastLookedUpEmail = state.participant.email;
        if (participantError.kind === 'bad-status' && participantError.response.status === 404) {
          this.state = {
            kind: 'ready',
            participant: state.participant,
            areLicensesAuditor: state.areLicensesAuditor,
            formErrors: state.formErrors,
            isExistingParticipant: just(false),
            initialGroupCount: just(0),
            initialLicenseCount: just(0),
          };
        } else {
          this.state = {
            kind: 'directory-lookup-error',
            participant: state.participant,
            areLicensesAuditor: state.areLicensesAuditor,
            message: this.lookupErrorMessage(participantError),
            formErrors: state.formErrors,
            isExistingParticipant: state.isExistingParticipant,
            initialGroupCount: state.initialGroupCount,
            initialLicenseCount: state.initialLicenseCount,
          };
        }
      });
    };

  private lookupErrorMessage = (participantError: ParticipantError): TPlainTextKey => {
    switch (participantError.kind) {
      case 'invalid-email-error':
      case 'missing-email-error':
        return 'A valid email address is required';
      case 'bad-status':
        return this.lookupBadStatusMessage(participantError.response.status);
      case 'missing-api-compatibility':
      case 'missing-application-id':
      case 'missing-link-error':
      case 'network-error':
      case 'bad-payload':
      case 'bad-url':
      case 'invalid-url-error':
      case 'timeout':
        return 'An error occurred, please refresh the page and try again';
    }
  };

  private lookupBadStatusMessage = (status: number): TPlainTextKey => {
    switch (status) {
      case 400:
        return 'Email domain is not available for this organization; contact support if you need further assistance';
      case 403:
        return 'Email address not available for this organization; contact support if you need further assistance';
      default:
        return 'An error occurred, please refresh the page and try again';
    }
  };

  @action
  create = () => {
    switch (this.state.kind) {
      case 'ready':
        this.state = creating(this.state);
        Task.succeed<ParticipantError, ReadonlyArray<Link>>(currentUserStore.links)
          .andThen(findLinkT('v2-create-participant'))
          .andThen(
            callApi(participantResourceDecoder, {
              participant: {
                first_name: this.state.participant.firstName,
                last_name: this.state.participant.lastName,
                email: this.state.participant.email,
                user_groups: this.state.participant.userGroups
                  .filter((group) => group.kind === 'selected' || group.kind === 'new')
                  .map((group) => {
                    if (group.kind === 'selected') {
                      return { id: group.group.id };
                    } else if (group.kind === 'new') {
                      return { name: group.name };
                    }
                    return {};
                  }),
                licenses: this.state.participant.licenses
                  .filter((license) => license.kind === 'selected')
                  .map((license) => ({
                    id: license.kind === 'selected' ? license.license.id : -1,
                  })),
                are_licenses_auditor: this.state.areLicensesAuditor,
              },
            }),
          )
          .fork(
            () => {
              runInAction(() => {
                this.state = {
                  kind: 'error',
                  participant: this.state.participant,
                  areLicensesAuditor: this.state.areLicensesAuditor,
                  message: 'An error occurred, please refresh the page and try again',
                  formErrors: this.state.formErrors,
                  isExistingParticipant: this.state.isExistingParticipant,
                  initialGroupCount: this.state.initialGroupCount,
                  initialLicenseCount: this.state.initialLicenseCount,
                };
              });
            },
            (participantResource: ParticipantResource) => {
              runInAction(() => {
                this.state = {
                  kind: 'confirming',
                  participant: participantResource.payload,
                  areLicensesAuditor: this.state.areLicensesAuditor,
                  formErrors: this.state.formErrors,
                  isExistingParticipant: this.state.isExistingParticipant,
                  initialGroupCount: this.state.initialGroupCount,
                  initialLicenseCount: this.state.initialLicenseCount,
                };
              });
            },
          );
        break;
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
      case 'directory-lookup-error':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  private validateAndUpdateFormErrors = (): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'directory-lookup-error':
      case 'error':
        const errors: {
          email?: TPlainTextKey;
          firstName?: TPlainTextKey;
          lastName?: TPlainTextKey;
        } = {};

        // Validate email - only show error if field is not empty
        if (this.state.participant.email.trim().length > 0) {
          validateEmail(this.state.participant.email).cata({
            Err: () => {
              errors.email = 'A valid email address is required';
            },
            Ok: () => {},
          });
        }

        // Validate first name
        if (!this.state.participant.firstName || this.state.participant.firstName.trim() === '') {
          errors.firstName = 'First name is required';
        }

        // Validate last name
        if (!this.state.participant.lastName || this.state.participant.lastName.trim() === '') {
          errors.lastName = 'Last name is required';
        }

        this.state.formErrors = errors;
        break;
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateEmail = (email: string): void => {
    // Clear directory lookup error when email changes - replace entire state object
    if (this.state.kind === 'directory-lookup-error') {
      this.state = {
        kind: 'ready',
        participant: this.state.participant,
        areLicensesAuditor: this.state.areLicensesAuditor,
        formErrors: {},
        isExistingParticipant: this.state.isExistingParticipant,
        initialGroupCount: this.state.initialGroupCount,
        initialLicenseCount: this.state.initialLicenseCount,
      };
    } else if (this.state.kind === 'error') {
      this.state = {
        kind: 'ready',
        participant: this.state.participant,
        areLicensesAuditor: this.state.areLicensesAuditor,
        formErrors: {},
        isExistingParticipant: this.state.isExistingParticipant,
        initialGroupCount: this.state.initialGroupCount,
        initialLicenseCount: this.state.initialLicenseCount,
      };
    }

    this.state.participant.email = email;
    // Reset the last looked up email when email changes so we can lookup new emails
    if (email !== this.lastLookedUpEmail) {
      this.lastLookedUpEmail = '';
    }
    this.validateAndUpdateFormErrors();
  };

  @action
  updateFirstName = (firstName: string): void => {
    this.state.participant.firstName = firstName;
    this.validateAndUpdateFormErrors();
  };

  @action
  updateLastName = (lastName: string): void => {
    this.state.participant.lastName = lastName;
    this.validateAndUpdateFormErrors();
  };

  @action
  updateUserGroups = (userGroups: SelectableUserGroup[]): void => {
    switch (this.state.kind) {
      case 'ready':
        this.state.participant.userGroups = userGroups;
        break;
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
      case 'directory-lookup-error':
      case 'error':
        return;
      default:
        assertNever(this.state);
    }
  };

  @action
  addUserGroup = (userGroup: SelectableUserGroup): void => {
    switch (this.state.kind) {
      case 'ready':
        this.state.participant.userGroups.push(userGroup);
        break;
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
      case 'directory-lookup-error':
      case 'error':
        return;
      default:
        assertNever(this.state);
    }
  };

  @action
  removeUserGroupAtIndex = (index: number): void => {
    switch (this.state.kind) {
      case 'ready':
        this.state.participant.userGroups.splice(index, 1);
        break;
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
      case 'directory-lookup-error':
      case 'error':
        return;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateUserGroupAtIndex = (index: number, userGroup: SelectableUserGroup): void => {
    switch (this.state.kind) {
      case 'ready':
        if (index >= 0 && index < this.state.participant.userGroups.length) {
          this.state.participant.userGroups[index] = userGroup;
        }
        break;
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
      case 'directory-lookup-error':
      case 'error':
        return;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateLicenses = (licenses: SelectableLicense[]): void => {
    switch (this.state.kind) {
      case 'ready':
        this.state.participant.licenses = licenses;
        break;
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
      case 'directory-lookup-error':
      case 'error':
        return;
      default:
        assertNever(this.state);
    }
  };

  @action
  addLicense = (license: SelectableLicense): void => {
    switch (this.state.kind) {
      case 'ready':
        this.state.participant.licenses.push(license);
        break;
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
      case 'directory-lookup-error':
      case 'error':
        return;
      default:
        assertNever(this.state);
    }
  };

  @action
  removeLicenseAtIndex = (index: number): void => {
    switch (this.state.kind) {
      case 'ready':
        this.state.participant.licenses.splice(index, 1);
        break;
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
      case 'directory-lookup-error':
      case 'error':
        return;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateLicenseAtIndex = (index: number, license: SelectableLicense): void => {
    switch (this.state.kind) {
      case 'ready':
        if (index >= 0 && index < this.state.participant.licenses.length) {
          this.state.participant.licenses[index] = license;
        }
        break;
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
      case 'directory-lookup-error':
      case 'error':
        return;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateAreLicensesAuditor = (areLicensesAuditor: boolean): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'directory-lookup-error':
      case 'error':
        this.state.areLicensesAuditor = areLicensesAuditor;
        break;
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
        return;
      default:
        assertNever(this.state);
    }
  };

  @action
  reset(): void {
    this.lastLookedUpEmail = '';
    this.state = {
      kind: 'ready',
      participant: {
        email: '',
        firstName: '',
        lastName: '',
        userGroups: [],
        licenses: [],
      },
      areLicensesAuditor: false,
      formErrors: {},
      isExistingParticipant: nothing(),
      initialGroupCount: nothing(),
      initialLicenseCount: nothing(),
    };
  }

  @action
  validateFirstName = (): void => {
    const trimmedValue = this.state.participant.firstName.trim();
    if (trimmedValue === '') {
      this.state.formErrors.firstName = 'First name is required';
    } else {
      delete this.state.formErrors.firstName;
    }
  };

  @action
  validateLastName = (): void => {
    const trimmedValue = this.state.participant.lastName.trim();
    if (trimmedValue === '') {
      this.state.formErrors.lastName = 'Last name is required';
    } else {
      delete this.state.formErrors.lastName;
    }
  };

  @computed
  get notification(): Maybe<FlashAlert> {
    switch (this.state.kind) {
      case 'error':
        return just(this.state).map(errorAlert);
      case 'ready':
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
      case 'directory-lookup-error':
        return nothing();
    }
  }

  @computed
  get hasFormErrors(): boolean {
    switch (this.state.kind) {
      case 'ready':
      case 'directory-lookup-error':
      case 'error':
        return !!(
          this.state.formErrors.email ||
          this.state.formErrors.firstName ||
          this.state.formErrors.lastName
        );
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
        return false;
    }
  }

  @computed
  get canSubmit(): boolean {
    const isSubmitting = this.state.kind === 'creating';
    const isLookingUp = this.state.kind === 'looking-for-participant';
    const hasDirectoryError = this.state.kind === 'directory-lookup-error';
    const hasEmail = this.state.participant.email.trim().length > 0;
    const hasFirstName = this.state.participant.firstName.trim().length > 0;
    const hasLastName = this.state.participant.lastName.trim().length > 0;
    const hasNoErrors = Object.keys(this.formErrors).length === 0;

    return (
      !isSubmitting &&
      !isLookingUp &&
      !hasDirectoryError &&
      hasEmail &&
      hasFirstName &&
      hasLastName &&
      hasNoErrors
    );
  }

  @computed
  get formErrors() {
    switch (this.state.kind) {
      case 'ready':
      case 'directory-lookup-error':
      case 'error':
        return this.state.formErrors;
      case 'looking-for-participant':
      case 'creating':
      case 'confirming':
        return {};
    }
  }
}

const participantStore = new ParticipantStore();

export { participantStore };
